import * as React from 'react';
import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';
import { FlexRow } from '../../Defaults/Flexbox/Flexbox';
import { __ } from 'react-i18n';
import { ZemplinLoaderWrapper } from '../../..';
import { TableWrapper } from '../General/TableWrapper';
import { TableRow, SecondTableTh } from '../General/Table';
import { DsiPurchaseRow as PurchaseRow } from './PurchaseRow';
import { Filters, Label } from './Invoices';
import { Select } from '../../Zemplin/General/Select';
import { Input } from '../../Zemplin/General/Input';
import * as moment from 'moment';

interface Props {
  orderedProducts: any;
  isFetching: boolean;
  handleAddToCart: any;
  brands?: any;
  categories?: any;
  refetchData?: any;
  showWithVat?: boolean;
  isB2C?: boolean;
  isB2B: boolean;
  user: any;
}

function Purchase({
  orderedProducts,
  isFetching,
  handleAddToCart,
  brands,
  categories,
  refetchData,
  showWithVat,
  isB2C,
  isB2B,
  user,
}: Props) {
  const date = new Date();
  date.setDate(date.getDate());
  const firstDate = new Date(date.getFullYear(), 1, 1);
  firstDate.setDate(firstDate.getDate());
  const today = moment();
  const [fromDate, setFromDate] = React.useState(
    today.subtract('3', 'months').format('YYYY-MM-DD'),
  );
  const [toDate, setToDate] = React.useState(moment().format('YYYY-MM-DD'));
  const [fromFormatted, setFromFormatted] = React.useState(null);
  const [toFormatted, setToFormatted] = React.useState(null);
  const [brandState, setBrandState] = React.useState('');
  const [categoryState, setCategoryState] = React.useState('');
  const columns = [
    { name: __('Produkt') },
    { name: __('Mes. priemer'), align: 'right' },
    { name: __('Množstvo'), align: 'right' },
    { name: '' },
    { name: '' },
    { name: '' },
  ];
  const headers =
    columns &&
    columns.map((item: any) => (
      <SecondTableTh
        width={item.width}
        minWidth={item.minWidth}
        align={item.align || 'left'}
        key={item.name}
      >
        {item.name}
      </SecondTableTh>
    ));

  const orderRows =
    orderedProducts &&
    orderedProducts.map(p => {
      return (
        <PurchaseRow
          key={p.product_id}
          data={p}
          managerLink={
            user && user.originalUser
              ? `/manager?customerId=${user.outer_id}-${user.id}&customerAddressId=`
              : null
          }
          handleAddToCart={handleAddToCart}
          showWithVat={showWithVat}
        />
      );
    });

  const onChangeBrand = e => {
    const {
      target: { value },
    } = e;
    setBrandState(value);
    refetchData({
      brandId: value,
      categId: categoryState,
      fromDate: fromFormatted || undefined,
      toDate: toFormatted || undefined,
    });
  };
  const onChangeCategory = e => {
    const {
      target: { value },
    } = e;
    setCategoryState(value);
    refetchData({
      brandId: brandState,
      categId: value,
      fromDate: fromFormatted || undefined,
      toDate: toFormatted || undefined,
    });
  };

  const changeFromDate = e => {
    const value = e.target.value;
    setFromDate(value);
    let formatted: any = new Date(value);
    const dd = String(formatted.getDate()).padStart(2, '0');
    const mm = String(formatted.getMonth() + 1).padStart(2, '0'); // January is 0!
    const yyyy = formatted.getFullYear();
    formatted = `${dd}-${mm}-${yyyy}`;
    setFromFormatted(formatted);

    refetchData({
      brandId: brandState,
      categId: categoryState,
      fromDate: formatted,
      toDate: toFormatted || undefined,
    });
  };

  const changeToDate = e => {
    const value = e.target.value;
    setToDate(value);
    let formatted: any = new Date(value);
    const dd = String(formatted.getDate()).padStart(2, '0');
    const mm = String(formatted.getMonth() + 1).padStart(2, '0'); // January is 0!
    const yyyy = formatted.getFullYear();
    formatted = `${dd}-${mm}-${yyyy}`;
    setToFormatted(formatted);
    refetchData({
      brandId: brandState,
      categId: categoryState,
      fromDate: fromFormatted || undefined,
      toDate: formatted,
    });
  };

  return (
    <Wrapper>
      <Filters>
        {brands && brands.length > 0 && (
          <Select
            value={brandState}
            options={[
              { name: '', value: '' },
              ...brands.map(b => ({
                name: b.brand_name,
                value: b.brand_id,
              })),
            ]}
            onChange={onChangeBrand}
            maxWidth={240}
            backgroundColor={'#FFFFFF'}
            marginRight={14}
            placeholder={__('Vyberte výrobcu')}
          />
        )}
        {categories && categories.length > 0 && (
          <Select
            value={categoryState}
            options={[
              { name: '', value: '' },
              ...categories.map(c => ({
                name: c.category_name,
                value: c.category_id,
              })),
            ]}
            onChange={onChangeCategory}
            maxWidth={240}
            backgroundColor={'#FFFFFF'}
            marginRight={14}
            placeholder={__('Vyberte kategóriu')}
          />
        )}
        {!!isB2B && (
          <>
            <Label>{`${__('od')}`}</Label>
            <Input
              min={'2021-07-01'}
              type="date"
              name={'from'}
              value={fromDate}
              onChange={changeFromDate}
              maxWidth={160}
            />
            <Label>{`${__('do')}`}</Label>
            <Input
              type="date"
              name={'to'}
              value={toDate}
              onChange={changeToDate}
              maxWidth={160}
            />
          </>
        )}
      </Filters>
      {isFetching ? (
        <ZemplinLoaderWrapper height={500} />
      ) : (
        <TableWrapper
          tableHasBorder={true}
          tableHasBoxShadow={true}
          noItemsText={__('Nenašli sa žiadne nákupy')}
          headers={headers}
          rows={orderRows}
          overflowX={true}
        />
      )}
    </Wrapper>
  );
}

const animateIn = keyframes`
   0% {
    opacity: 0;
    transform: scale(0.6) translateY(-8px);
  }
  
  100% {
    opacity: 1;
  }
`;

const Wrapper = styled(FlexRow)`
  flex-flow: row wrap;
  margin-bottom: ${rem(24)};
  animation: ${animateIn} 0.2s ease;
  width: 100%;

  ${({ theme }) => theme.mediab.m810`
      margin: 0;
      margin-bottom: ${({ visible }) => (visible ? rem(32) : 0)};
      margin-right: -${rem(16)};
  `};
`;

export { Purchase as DsiPurchase };
